import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { X, Mail, Trash2, Plus, Send, Check, ChevronRight, ChevronDown, Filter, Pencil } from 'lucide-react';
import { api } from '../../utils/api';
import API_ENDPOINTS from '../../config/apiConfig';
import { buildColumnModel } from './schedule/columnFacets';
import { describeSchedule, nextRuns } from './schedule/nextRuns';
import { PAGE_TIERS, PAGE_ROW_CAPS } from './schedule/rowDepth';
import { PERIODS, periodLabel, timezoneChoices } from './schedule/periods';
import { TimeframeBlock, DetailBlock, AttachmentBlock, ScheduleBlock, WhatLands, MAX_TIMES } from './schedule/ScheduleBlocks';
import './ReportsNative.css';

/**
 * Schedule-email modal — the native replacement for Power BI report
 * subscriptions. Creates `report-subscription` docs with
 * reportType 'nativePages': the executor renders every report page into one
 * PDF (RLS-scoped to the subscriber) and emails it on the chosen schedule.
 */

// PBI subscription parity: the legacy Power BI Revenue email opened on a
// yesterday-scoped Overview page and an MTD Trends page, so new schedules
// from those pages default to the matching rolling period (still editable).
const DEFAULT_PERIOD_BY_PAGE = {
  revenueOverview: 'yesterday',
  revenueTrends: 'mtd',
  // Forward-looking page: default to the live page's This Month window
  revenueForecast: 'thisMonth',
};

const hasFilterValue = (value) => Array.isArray(value) ? value.length > 0 : value !== null && value !== undefined && value !== '';
const sameOptionValue = (a, b) => String(a) === String(b);

function selectedFilterLabel(definition, value) {
  if (!hasFilterValue(value)) return definition.allLabel || 'All';
  if (definition.type === 'range') {
    const parts = [];
    if (value?.min !== '' && value?.min !== null && value?.min !== undefined) parts.push(`at least ${value.min}`);
    if (value?.max !== '' && value?.max !== null && value?.max !== undefined) parts.push(`at most ${value.max}`);
    return parts.join(' · ') || definition.allLabel || 'Any';
  }
  if (definition.type === 'rating') return `${value}+ stars`;
  const values = Array.isArray(value) ? value : [value];
  const labels = values.map((item) => definition.options?.find((option) => sameOptionValue(option.value, item))?.label || String(item));
  if (labels.length <= 2) return labels.join(', ');
  return `${labels.slice(0, 2).join(', ')} +${labels.length - 2}`;
}

function setParamPath(target, path, value) {
  const keys = Array.isArray(path) ? path : [path];
  let cursor = target;
  keys.slice(0, -1).forEach((key) => {
    if (!cursor[key] || typeof cursor[key] !== 'object' || Array.isArray(cursor[key])) cursor[key] = {};
    cursor = cursor[key];
  });
  const leaf = keys[keys.length - 1];
  if (hasFilterValue(value)) cursor[leaf] = value;
  else delete cursor[leaf];
  // Remove an empty nested filters object so the fetch envelope stays clean.
  if (target.filters && Object.keys(target.filters).length === 0) delete target.filters;
}

function ScopeFilterField({ definition, value, onChange }) {
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(false);
  const options = definition.options || [];
  const visibleOptions = search
    ? options.filter((option) => String(option.label).toLowerCase().includes(search.toLowerCase()))
    : options;

  if (definition.type === 'text') {
    return (
      <label className="rn-scope-field">
        <span>{definition.label}</span>
        <input
          value={value || ''}
          onChange={(event) => onChange(event.target.value)}
          placeholder={definition.placeholder || `Any ${definition.label.toLowerCase()}`}
        />
      </label>
    );
  }

  if (definition.type === 'range') {
    return (
      <div className="rn-scope-filter rn-scope-range">
        <div className="rn-scope-filter-head"><div><span>{definition.label}</span><b>{selectedFilterLabel(definition, value)}</b></div></div>
        <div>
          <label><span>Minimum</span><input type="number" value={value?.min ?? ''} onChange={(event) => onChange({ ...(value || {}), min: event.target.value })} placeholder="No minimum" /></label>
          <label><span>Maximum</span><input type="number" value={value?.max ?? ''} onChange={(event) => onChange({ ...(value || {}), max: event.target.value })} placeholder="No maximum" /></label>
        </div>
      </div>
    );
  }

  if (!definition.multiple) {
    return (
      <label className="rn-scope-field">
        <span>{definition.label}</span>
        <select value={value ?? ''} onChange={(event) => onChange(event.target.value)}>
          <option value="">{definition.allLabel || 'All'}</option>
          {options.map((option) => <option key={String(option.value)} value={option.value}>{option.label}</option>)}
        </select>
      </label>
    );
  }

  const selected = Array.isArray(value) ? value : [];
  const allSelected = selected.length === 0;
  return (
    <div className={`rn-scope-filter${expanded ? ' rn-expanded' : ''}`}>
      <div className="rn-scope-filter-head rn-scope-filter-summary">
        <label>
          <input
            type="checkbox"
            checked={allSelected}
            onChange={() => {
              onChange([]);
              setExpanded(false);
              setSearch('');
            }}
          />
          <span>
            <b>{definition.allLabel || `All ${definition.label.toLowerCase()}`}</b>
            <small>{allSelected ? 'Included automatically' : selectedFilterLabel(definition, selected)}</small>
          </span>
        </label>
        <button type="button" className="rn-scope-expand" onClick={() => setExpanded((current) => !current)}>
          {allSelected ? 'Configure' : `${selected.length} selected`}
          {expanded ? <ChevronDown /> : <ChevronRight />}
        </button>
      </div>
      {expanded && options.length > 8 && (
        <input
          className="rn-scope-search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={`Find ${definition.label.toLowerCase()}…`}
        />
      )}
      {expanded && <div className="rn-scope-options">
        {visibleOptions.map((option) => {
          const checked = selected.some((item) => sameOptionValue(item, option.value));
          return (
            <label key={String(option.value)} className={checked ? 'rn-selected' : ''}>
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onChange(checked ? selected.filter((item) => !sameOptionValue(item, option.value)) : [...selected, option.value])}
              />
              <span>{option.label}</span>
              {checked && <Check />}
            </label>
          );
        })}
        {visibleOptions.length === 0 && <div className="rn-scope-empty">No matching options.</div>}
      </div>}
    </div>
  );
}

export function ReportScheduleModal({ open, onClose, organizationId, orgName, userEmail, reportView }) {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const [name, setName] = useState('');
  const browserTimezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const defaultTrigger = useCallback(() => ({
    frequency: 'daily',
    // daily means every day: scheduleUtils fires only on listed days, so a
    // partial list would quietly turn a daily schedule into a weekly one
    days: [0, 1, 2, 3, 4, 5, 6],
    times: ['08:00'],
    timezone: browserTimezone,
  }), [browserTimezone]);
  const [trigger, setTrigger] = useState(defaultTrigger);
  const [period, setPeriod] = useState('currentView');
  // null = the email body covers the same window as the attachment
  const [snapshotPeriod, setSnapshotPeriod] = useState(null);
  // null = open every level and let the print cap decide, as before
  const [expandLevel, setExpandLevel] = useState(null);
  const [attachmentFormat, setAttachmentFormat] = useState('pdf');
  const [ccInput, setCcInput] = useState('');
  const [filterValues, setFilterValues] = useState({});
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [editingSub, setEditingSub] = useState(null);

  const timezone = browserTimezone;
  const effectiveTimezone = trigger.timezone || timezone;
  const columnModel = useMemo(() => buildColumnModel(reportView?.columns), [reportView?.columns]);
  // A page can name its own tiers; otherwise the shared map keyed by pageKind
  // does, so the ten report pages only have to hand over their row counts.
  const tiers = reportView?.tiers || PAGE_TIERS[reportView?.pageKind] || null;
  const rowCounts = reportView?.rowCountsByLevel || null;
  const effectiveLevel = Number.isInteger(expandLevel) ? expandLevel : (tiers ? tiers.length - 1 : 0);
  const rowEstimate = rowCounts ? rowCounts[Math.min(effectiveLevel, rowCounts.length - 1)] : null;
  // A subscription saved in another zone (Tokyo, edited from Chicago) keeps
  // its own zone in the list, or the select would show the wrong one.
  const savedTimezone = editingSub?.triggerTime?.timezone;
  const TIMEZONES = useMemo(() => timezoneChoices(savedTimezone, browserTimezone), [savedTimezone, browserTimezone]);

  // Everything the pinned footer reads back, so the user can see what lands
  // without scrolling up.
  const previewRuns = useMemo(
    () => nextRuns(trigger, { count: 1, closeDay: reportView?.financialCloseDay ?? 15 }),
    [trigger, reportView?.financialCloseDay],
  );
  const snapshotLabel = useMemo(() => {
    const value = snapshotPeriod || period;
    if (value === 'currentView') return reportView?.periodLabel || 'this view\u2019s range';
    return periodLabel(value);
  }, [snapshotPeriod, period, reportView?.periodLabel]);
  const fileName = useMemo(() => {
    const base = (name || reportView?.title || 'Report').replace(/[\\/:*?"<>|]/g, '-');
    return `${base}.${attachmentFormat}`;
  }, [name, reportView?.title, attachmentFormat]);
  const recipientCount = useMemo(
    () => 1 + ccInput.split(/[,;\s]+/).filter((value) => /.+@.+\..+/.test(value)).length,
    [ccInput],
  );
  const filterDefinitionSignature = JSON.stringify(reportView?.filters || []);
  // Parent report pages construct reportView inline; stabilize equivalent
  // filter catalogs so an open builder never resets while the user edits it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filterDefinitions = useMemo(() => reportView?.filters || [], [filterDefinitionSignature]);

  const resetBuilder = useCallback(() => {
    setEditingSub(null);
    setPeriod(DEFAULT_PERIOD_BY_PAGE[reportView?.pageKind] || 'currentView');
    setSnapshotPeriod(null);
    setExpandLevel(null);
    setAttachmentFormat('pdf');
    setSelectedColumns(reportView?.visibleColumns || (reportView?.columns || []).map((column) => column.key));
    setName('');
    setTrigger(defaultTrigger());
    setCcInput('');
    setFilterValues(Object.fromEntries(filterDefinitions.map((definition) => [definition.key, definition.value ?? (definition.multiple ? [] : '')])));
  }, [filterDefinitions, reportView?.pageKind, reportView?.visibleColumns, reportView?.columns, defaultTrigger]);

  // Prefill the builder from a saved subscription so it can be edited in place.
  const startEdit = useCallback((sub) => {
    const readPath = (obj, path) => path.reduce((cursor, key) => (cursor && typeof cursor === 'object' ? cursor[key] : undefined), obj);
    const savedParams = sub.reportConfig?.params || {};
    const values = {};
    filterDefinitions.forEach((definition) => {
      const path = definition.paramPath || (definition.type === 'range' ? ['filters', definition.key] : [definition.key]);
      if (definition.type === 'range') {
        const leaf = path[path.length - 1];
        const base = path.slice(0, -1);
        const min = readPath(savedParams, [...base, `${leaf}Min`]);
        const max = readPath(savedParams, [...base, `${leaf}Max`]);
        values[definition.key] = min !== undefined || max !== undefined ? { min: min ?? '', max: max ?? '' } : '';
        return;
      }
      let stored = readPath(savedParams, path);
      // toParam has no declared inverse — recover the UI value by matching
      // each option's transformed form against what was saved.
      if (stored !== undefined && definition.toParam && definition.options) {
        const match = definition.options.find((option) => JSON.stringify(definition.toParam(option.value)) === JSON.stringify(stored));
        if (match) stored = match.value;
      }
      values[definition.key] = stored !== undefined ? stored : (definition.multiple ? [] : '');
    });

    const saved = sub.triggerTime || {};
    const emailConfig = sub.deliveryConfig?.emailConfig || {};
    setEditingSub(sub);
    setName(sub.name || emailConfig.customSubject || '');
    setTrigger({
      frequency: saved.frequency || 'daily',
      days: Array.isArray(saved.days) && saved.days.length
        ? saved.days
        : (saved.frequency === 'monthly' ? [1] : [0, 1, 2, 3, 4, 5, 6]),
      times: Array.isArray(saved.times) && saved.times.length ? saved.times.slice(0, MAX_TIMES) : ['08:00'],
      timezone: saved.timezone || browserTimezone,
      ...(saved.anchor ? { anchor: saved.anchor } : {}),
    });
    setPeriod(sub.period || 'mtd');
    setSnapshotPeriod(sub.reportConfig?.snapshotPeriod || null);
    setExpandLevel(Number.isInteger(sub.reportConfig?.expandLevel) ? sub.reportConfig.expandLevel : null);
    setAttachmentFormat(emailConfig.attachmentFormat === 'xlsx' ? 'xlsx' : 'pdf');
    setCcInput((emailConfig.ccRecipients || []).join(', '));
    setSelectedColumns(sub.reportConfig?.visibleColumns || reportView?.visibleColumns || (reportView?.columns || []).map((column) => column.key));
    setFilterValues(values);
    setError(null);
    setShowForm(true);
  }, [filterDefinitions, reportView?.visibleColumns, reportView?.columns, browserTimezone]);

  const loadSubs = useCallback(async () => {
    if (!organizationId || !userEmail) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`${API_ENDPOINTS.REPORT_SUBSCRIPTIONS}?orgId=${organizationId}&userId=${encodeURIComponent(userEmail)}`);
      const all = Array.isArray(res.data) ? res.data : [];
      setSubs(all.filter((sub) => {
        if (sub.reportType !== 'nativePages') return false;
        if (!reportView?.pageKind) return true;
        if (sub.pageKinds === 'all') return true;
        return (Array.isArray(sub.pageKinds) ? sub.pageKinds : [sub.pageKinds]).includes(reportView.pageKind);
      }));
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [organizationId, userEmail, reportView?.pageKind]);

  useEffect(() => {
    if (open) {
      loadSubs();
      setShowForm(false);
      resetBuilder();
    }
  }, [open, loadSubs, resetBuilder]);

  const scopeSummary = useMemo(() => {
    const rangeLabel = period === 'currentView'
      ? reportView?.periodLabel || 'Current report range'
      : periodLabel(period);
    return [
      { label: 'Date range', value: rangeLabel },
      ...filterDefinitions.map((definition) => ({ label: definition.label, value: selectedFilterLabel(definition, filterValues[definition.key]) })),
      { label: 'Columns', value: `${selectedColumns.length || 'Current'} selected, in saved order` },
    ];
  }, [period, reportView?.periodLabel, filterDefinitions, filterValues, selectedColumns.length]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    const cc = ccInput.split(/[,;\s]+/).map((s) => s.trim()).filter((s) => /.+@.+\..+/.test(s));
    // An anchored monthly schedule derives its day per month, so it carries
    // no `days` list; everything else keeps the one the blocks maintain.
    const triggerTime = trigger.anchor
      ? { frequency: trigger.frequency, times: trigger.times, timezone: trigger.timezone, anchor: trigger.anchor }
      : { frequency: trigger.frequency, days: trigger.days, times: trigger.times, timezone: trigger.timezone };
    // Editing starts from the subscription's saved scope, not the live page view.
    const savedParams = JSON.parse(JSON.stringify((editingSub ? editingSub.reportConfig?.params : reportView?.params) || {}));
    if (period !== 'currentView') {
      delete savedParams.startDate;
      delete savedParams.endDate;
      delete savedParams.asOfDate;
    }
    filterDefinitions.forEach((definition) => {
      const rawValue = filterValues[definition.key];
      if (definition.type === 'range') {
        const path = definition.paramPath || ['filters', definition.key];
        const leaf = path[path.length - 1];
        setParamPath(savedParams, [...path.slice(0, -1), `${leaf}Min`], rawValue?.min);
        setParamPath(savedParams, [...path.slice(0, -1), `${leaf}Max`], rawValue?.max);
        return;
      }
      const savedValue = definition.toParam ? definition.toParam(rawValue) : rawValue;
      setParamPath(savedParams, definition.paramPath || [definition.key], savedValue);
    });
    const filtersLine = scopeSummary
      .filter((item) => item.label !== 'Columns')
      .map((item) => `${item.label}: ${item.value}`)
      .join(' · ');
    const subject = name || `${orgName || 'Lodgeic'} Report`;
    const payload = {
      period: period === 'currentView' ? 'mtd' : period,
      reportConfig: {
        params: savedParams,
        visibleColumns: selectedColumns,
        filtersLine,
        filterSummary: scopeSummary,
        periodLabel: period === 'currentView' ? reportView?.periodLabel || '' : '',
        // absent means "same window as the attachment" / "open every level"
        ...(snapshotPeriod ? { snapshotPeriod } : {}),
        ...(Number.isInteger(expandLevel) ? { expandLevel } : {}),
      },
      name: subject,
      triggerTime,
      deliveryConfig: {
        deliveryMethod: 'email',
        emailConfig: {
          ...(editingSub?.deliveryConfig?.emailConfig || {}),
          ccRecipients: cc,
          customSubject: subject,
          includeAttachment: true,
          attachmentFormat,
        },
      },
    };
    try {
      if (editingSub) {
        await api.patch(API_ENDPOINTS.REPORT_SUBSCRIPTIONS, {
          id: editingSub.id,
          userId: editingSub.userId,
          ...payload,
        });
      } else {
        await api.post(API_ENDPOINTS.REPORT_SUBSCRIPTIONS, {
          userId: userEmail,
          orgId: organizationId,
          orgName: orgName || '',
          reportType: 'nativePages',
          pageKinds: reportView?.pageKind ? [reportView.pageKind] : 'all',
          isActive: true,
          ...payload,
          deliveryConfig: {
            ...payload.deliveryConfig,
            emailConfig: { ...payload.deliveryConfig.emailConfig, bccRecipients: [] },
          },
        });
      }
      setName('');
      setCcInput('');
      setEditingSub(null);
      setShowForm(false);
      await loadSubs();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (sub) => {
    try {
      await api.patch(API_ENDPOINTS.REPORT_SUBSCRIPTIONS, { id: sub.id, userId: sub.userId, isActive: !sub.isActive });
      await loadSubs();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const handleDelete = async (sub) => {
    try {
      await api.delete(API_ENDPOINTS.REPORT_SUBSCRIPTIONS, { data: { id: sub.id, userId: sub.userId } });
      await loadSubs();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const [testingId, setTestingId] = useState(null);
  const [notice, setNotice] = useState(null);
  const handleSendTest = async (sub) => {
    setTestingId(sub.id);
    setError(null);
    setNotice(null);
    try {
      // renders the native PDF book NOW and emails it to you — the Power BI
      // replacement preview. The scheduled job is unaffected.
      const res = await api.post(API_ENDPOINTS.NATIVE_REPORT_SUBSCRIPTION_TEST(sub.id));
      const d = res.data || {};
      setNotice(
        `✓ Test sent to ${userEmail} — ${d.pageCount || 0} pages (${(d.pageTitles || []).join(', ')})${d.failedPages?.length ? ` · ${d.failedPages.length} skipped` : ''}`
      );
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setTestingId(null);
    }
  };

  if (!open) return null;

  return (
    <div className="rn-sch-scrim" onClick={onClose}>
      <aside
        className="rn-sch-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Schedule report email"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="rn-modal-head">
          <Mail />
          <h2>Scheduled report emails</h2>
          <div className="rn-spacer" />
          <button type="button" className="rn-icon-btn" title="Close" onClick={onClose}>
            <X />
          </button>
        </div>
        <div className="rn-modal-sub">
          A focused PDF of <b>{reportView?.title || 'this report'}</b>, on your schedule — scoped to what <b>{userEmail}</b> can see in Lodgeic.
        </div>

        {error && <div className="rn-modal-error">{error}</div>}
        {notice && <div className="rn-modal-notice">{notice}</div>}

        {!showForm && (
          <>
            <div className="rn-modal-list">
              {loading && <div className="rn-empty" style={{ padding: 16 }}>Loading…</div>}
              {!loading && subs.length === 0 && (
                <div className="rn-empty" style={{ padding: 16 }}>No scheduled emails yet.</div>
              )}
              {!loading &&
                subs.map((sub) => (
                  <div key={sub.id} className="rn-modal-row">
                    <div className="rn-modal-row-main">
                      <div className="rn-modal-row-title">
                        {sub.name || sub.deliveryConfig?.emailConfig?.customSubject || 'Report subscription'}
                        {!sub.isActive && <span className="rn-modal-paused">paused</span>}
                      </div>
                      <div className="rn-modal-row-sub">
                        {describeSchedule(sub.triggerTime)}
                        {sub.period ? ` · ${periodLabel(sub.period)}` : ''}
                        {sub.nextScheduled && sub.isActive
                          ? ` · next ${new Date(sub.nextScheduled).toLocaleString()}`
                          : ''}
                      </div>
                      {sub.reportConfig?.filterSummary?.length > 0 && (
                        <div className="rn-modal-row-scope">
                          {sub.reportConfig.filterSummary.slice(0, 4).map((item) => (
                            <span key={item.label}><b>{item.label}</b> {item.value}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      className="rn-btn rn-btn-sm"
                      onClick={() => handleSendTest(sub)}
                      disabled={testingId === sub.id}
                      title="Render the native PDF now and email it to you (preview of the Power BI replacement)"
                    >
                      <Send size={13} />
                      {testingId === sub.id ? 'Sending…' : 'Send test'}
                    </button>
                    <button type="button" className="rn-btn rn-btn-sm" onClick={() => handleToggle(sub)}>
                      {sub.isActive ? 'Pause' : 'Resume'}
                    </button>
                    <button type="button" className="rn-icon-btn" title="Edit subscription" onClick={() => startEdit(sub)}>
                      <Pencil />
                    </button>
                    <button type="button" className="rn-icon-btn" title="Delete subscription" onClick={() => handleDelete(sub)}>
                      <Trash2 />
                    </button>
                  </div>
                ))}
            </div>
            <div className="rn-modal-foot">
              <button type="button" className="rn-btn rn-btn-primary" onClick={() => { resetBuilder(); setShowForm(true); }}>
                <Plus /> New scheduled email
              </button>
            </div>
          </>
        )}

        {showForm && (
          <>
            <div className="rn-sch-body">
              <div className="rn-step-intro">
                <Filter />
                <div>
                  <h3>{reportView?.title || 'Report'}</h3>
                  <p>These settings belong to this schedule. Changing the live report later will not change what gets sent.</p>
                </div>
              </div>

              <label className="rn-modal-field">
                <span>Email subject</span>
                <input value={name} onChange={(event) => setName(event.target.value)} placeholder={`${reportView?.title || orgName || 'Lodgeic'} report`} />
              </label>

              <TimeframeBlock
                periods={PERIODS}
                period={period}
                snapshotPeriod={snapshotPeriod}
                onPeriod={setPeriod}
                onSnapshotPeriod={setSnapshotPeriod}
                currentViewLabel={reportView?.periodLabel}
              />

              {filterDefinitions.length > 0 && (
                <section className="rn-sch-block">
                  <header>
                    <h3>Scope</h3>
                    <p>Which properties and filters this schedule saves.</p>
                  </header>
                  {filterDefinitions.map((definition) => (
                    <ScopeFilterField
                      key={definition.key}
                      definition={definition}
                      value={filterValues[definition.key]}
                      onChange={(value) => setFilterValues((current) => ({ ...current, [definition.key]: value }))}
                    />
                  ))}
                </section>
              )}

              <DetailBlock
                model={columnModel}
                selectedColumns={selectedColumns}
                onColumns={setSelectedColumns}
                tiers={tiers}
                expandLevel={expandLevel}
                onExpandLevel={setExpandLevel}
                rowEstimate={rowEstimate}
                // Excel has no page to run off, so only the PDF sheds levels for length
                rowCap={attachmentFormat === 'xlsx' ? Infinity : reportView?.rowCap ?? PAGE_ROW_CAPS[reportView?.pageKind] ?? 260}
                expandDisabledReason={null}
              />

              <AttachmentBlock
                format={attachmentFormat}
                onFormat={setAttachmentFormat}
                pageCount={reportView?.pageCount}
              />

              <ScheduleBlock
                trigger={trigger}
                onTrigger={setTrigger}
                timezones={TIMEZONES}
                closeDay={reportView?.financialCloseDay ?? 15}
              />

              <label className="rn-modal-field">
                <span>CC recipients (optional)</span>
                <input value={ccInput} onChange={(event) => setCcInput(event.target.value)} placeholder="gm@hotel.com, owner@group.com" />
                <small>Separate addresses with commas. Recipients receive the same permission-scoped file.</small>
              </label>

              <div className="rn-permission-note">
                <Check /> Data is resolved when the email runs and remains limited to what {userEmail} can access.
              </div>
            </div>

            <div className="rn-sch-foot">
              <WhatLands
                subject={name || `${reportView?.title || orgName || 'Lodgeic'} report`}
                snapshotLabel={snapshotLabel}
                attachmentLabel={attachmentFormat === 'xlsx' ? 'Excel' : 'PDF'}
                fileName={fileName}
                nextRun={previewRuns[0] || null}
                timezone={effectiveTimezone}
                recipientCount={recipientCount}
              />
              <div className="rn-sch-actions">
                <button type="button" className="rn-btn rn-btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : editingSub ? 'Save changes' : 'Schedule email'}
                </button>
                <button type="button" className="rn-btn" onClick={() => { setShowForm(false); setEditingSub(null); }} disabled={saving}>Cancel</button>
              </div>
            </div>
          </>
        )}
      </aside>
    </div>
  );
}

export default ReportScheduleModal;
